import { getCurrentUser } from '@/api/user'
import { getAccountContacts } from '@/api/accountEndpoints'
import { PermissionEnum } from '@/app/core/enums/PermissionEnum'
import { useStorage } from '@vueuse/core'
import { isEqual, omit } from 'lodash'
import { defineStore } from 'pinia'
import type { Entries } from 'type-fest'

// Updated interface to match actual /api/users/me API response
export interface LoadboardUserInfo {
  id: number
  name: string
  surname: string
  phone: string | null
  email: string
  temp_account_id: number | null
  photo: string | null
  last_active_at: string | null
  permissions: string[]
  roles: { id: number; name: string }[]
  last_login: {
    id: number
    user_id: number
    email: string
    ip: string
    created_at: string
  }
  account: {
    id: number
    manager_user_id: number | null
    company_name: string
    dba_name: string | null
    mc_number: string
    address: string | null
    city: string | null
    state: string | null
    zip: string | null
    phone: string | null
    email: string | null
    photo: string | null
    approved_at: string | null
    deleted_at: string | null
    documents: any[]
    permissions: any[]
  }
}

export interface LoginResponse {
  data: {
    user: LoadboardUserInfo
    token: string
  }
}

const emptyUserData = {
  id: 0,
  name: '',
  surname: '',
  phone: null,
  email: '',
  temp_account_id: null,
  photo: null,
  last_active_at: null,
  permissions: [],
  roles: [],
  last_login: {
    id: 0,
    user_id: 0,
    email: '',
    ip: '',
    created_at: ''
  },
  account: {
    id: 0,
    manager_user_id: null,
    company_name: '',
    dba_name: null,
    mc_number: '',
    address: null,
    city: null,
    state: null,
    zip: null,
    phone: null,
    email: null,
    photo: null,
    approved_at: null,
    deleted_at: null,
    documents: [],
    permissions: []
  }
} as LoadboardUserInfo

export const useUserStore = defineStore('user', {
  state: () => ({
    data: useStorage('user', JSON.parse(JSON.stringify(emptyUserData)) as LoadboardUserInfo),
    token: useStorage('token', ''),
    paymentContact: null as { id: number; first_name: string; phone: string; email: string } | null
  }),

  getters: {
    getFullName: state => state.data.name + ' ' + state.data.surname,
    user: state => state.data,
    getToken: state => state.token,
    getAvatarFetchUrl: state => {
      if (!state.data.photo) {
        return "/static/icons/shared/avatar.svg";
      }
      // Use S3 bucket origin from environment
      const avatarsBucketOrigin = process.env.VUE_APP_S3_AVATARS_ORIGIN || '';
      return avatarsBucketOrigin + state.data.photo;
    },
    hasPaymentContact: state => !!(state.paymentContact?.first_name || state.paymentContact?.email || state.paymentContact?.phone),
    tokenCan: state => (permission: string) => {
      // Convert string array to object lookup for compatibility
      const permissionsObj = Object.create(null)
      for (const perm of state.data.permissions) {
        permissionsObj[perm] = true
      }
      return permissionsObj[permission] || permissionsObj.admin || false
    }
  },

  actions: {
    setStateAfterLogin({ data }: LoginResponse) {
      const obj = Object.assign({}, data.user)
      // Keep permissions as string array (loadboard format)
      Object.assign(this.data, obj)
      this.token = data.token
      // Also update localStorage for compatibility with existing interceptors
      localStorage.setItem('token', data.token)
    },
    
    async updateStore() {
      try {
        // The payment contact is account-level: skip the fetch without
        // account.manage rather than let it 403.
        const [userResponse, contactResponse] = await Promise.all([
          getCurrentUser(),
          this.tokenCan(PermissionEnum.AccountManage)
            ? getAccountContacts({ type: 5 }).catch(() => null)
            : null
        ])
        Object.assign(this.data, userResponse)
        if (contactResponse?.data?.length > 0) {
          const c = contactResponse.data[0]
          this.paymentContact = {
            id: c.id,
            first_name: c.contact?.first_name || '',
            phone: c.contact?.phone || '',
            email: c.contact?.email || ''
          }
        }
      } catch (error) {
        console.error('Failed to update user store:', error)
        throw error
      }
    },
    
    cleanAll() {
      this.data = JSON.parse(JSON.stringify(emptyUserData))
      this.token = ''
      this.paymentContact = null
      // Also clear localStorage for compatibility
      localStorage.removeItem('token')
    },
    
    updateUserPhoto(photoName: string) {
      this.data.photo = photoName
    },
    
    getAlteredSettings(updated: Partial<LoadboardUserInfo>) {
      const updatedValues = {} as any
      console.log({ updated, store: useUserStore().user })

      const { account: updAccountSettings, ...updUserSettings } = updated
      const { account: currAccountSettings, ...currUserSettings } = useUserStore().user

      for (const [key, value] of Object.entries(
        updUserSettings
      ) as Entries<Omit<LoadboardUserInfo, 'account'>>) {
        if (
          updUserSettings[key] !== undefined &&
          !isEqual(currUserSettings[key], updUserSettings[key])
        ) {
          Object.assign(updatedValues, { [key]: value })
        }
      }

      if (updAccountSettings) {
        for (const [key, value] of Object.entries(
          updAccountSettings
        ) as Entries<LoadboardUserInfo['account']>) {
          if (
            updAccountSettings[key] !== undefined &&
            !isEqual(currAccountSettings[key], updAccountSettings[key])
          ) {
            Object.assign(updatedValues, { account: { [key]: value } })
          }
        }
      }

      const filterUnsecure = omit(updatedValues, ['id', 'email', 'account.id', 'photo'])

      return filterUnsecure
    }
  }
})